import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import axios from "../Axios";
import { isValidUrl } from "../Components/Utility";
import { Urls } from "../Urls";
import { API_STORAGE } from "../Components/Constants";
import { excelSerialFromYMD, extractYMD } from "./dateUtils";

const AZURE_CONTAINER_NAME = "private-container";

export const downloadFileToLocal = async (url, fileName = "") => {
  if (!url || !isValidUrl(url)) {
    console.error("downloadFile: Invalid or missing URL.");
    return;
  }

  if (fileName === "") {
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  } else {
    try {
      // extract file extension from the URL - ignore query params
      const urlParts = url.split("?");
      const path = urlParts[0];
      const extMatch = path.match(/\.(\w+)(?:$|\?)/);
      const extension = extMatch ? `.${extMatch[1]}` : "";

      // Append extension if not already present
      if (fileName !== "" && !fileName.endsWith(extension)) {
        fileName += extension;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch file.");

      const blob = await response.blob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  }
};

export const getSignedDownloadUrl = async (filename) => {
  if (!filename) return null;

  try {
    const body = {
      action: "download",
      fileName: filename,
      accountName: API_STORAGE,
      containerName: AZURE_CONTAINER_NAME,
    };

    const { data } = await axios.post(Urls.uploadServiceNew, body);
    return data?.url || null;
  } catch (error) {
    console.error("Error fetching signed URL:", error);
    return null;
  }
};

/**
 * Fetches signed URL and downloads the file to local machine.
 */
export const DownloadFile = async (
  filename,
  toggleLoader,
  downloadedFileName
) => {
  if (!filename) return;

  toggleLoader && toggleLoader(true);

  try {
    const url = await getSignedDownloadUrl(filename);
    if (url) await downloadFileToLocal(url, downloadedFileName);
    else throw new Error("Failed to get signed URL");
  } catch (error) {
    console.error(error);
    toast.error("Failed to fetch Document");
  } finally {
    toggleLoader && toggleLoader(false);
  }
};

export function exportRPTResultsToExcel(lanes, results) {
  // --- Branding + header rows ---
  const title = "LoRRI — Bulk Rate Forecasts";
  const generatedOn = `Generated: ${new Date().toLocaleString("en-IN")}`;
  const headers = [
    "#",
    "Origin",
    "Destination",
    "Truck",
    "Date",
    "Forecasted Rate (INR)",
  ];

  // Build AOA: title row, generated row, blank, headers, then data
  const aoa = [[title], [generatedOn], [""], headers];

  // Data rows (align with your table)
  const rows = lanes.map((lane, idx) => {
    const res = results[idx];
    const dateObj = new Date(lane.date); // we’ll set Excel date type below
    const price = res && !res.error ? Number(res.predicted_base_price) : null;

    return [
      idx + 1,
      lane?.origin_details?.location_name || "",
      lane?.destination_details?.location_name || "",
      lane?.truck_type || "",
      dateObj, // will mark as Excel Date
      price, // will format as currency if not null
    ];
  });

  aoa.push(...rows);

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Merge title across A1:F1
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

  // Column widths (tweak to taste)
  ws["!cols"] = [
    { wch: 5 }, // #
    { wch: 28 }, // Origin
    { wch: 28 }, // Destination
    { wch: 20 }, // Truck
    { wch: 14 }, // Date
    { wch: 18 }, // Forecasted Rate
  ];

  // Auto-filter on header row
  const headerRowIndex = 3; // 0-based: row 4 in Excel
  const lastRow = headerRowIndex + rows.length;
  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRowIndex, c: 0 },
      e: { r: headerRowIndex, c: headers.length - 1 },
    }),
  };

  // Apply data types & number formats for Date and Currency
  // Dates are in column 4 (0-based index = 4), prices in column 5 (index = 5)
  // Apply data types & number formats for Date and Currency
  for (let r = 0; r < rows.length; r++) {
    const excelRow = headerRowIndex + 1 + r; // first data row index in sheet

    // --- DATE (column index 4) ---
    const dateCellRef = XLSX.utils.encode_cell({ r: excelRow, c: 4 });
    const ymd = extractYMD(rows[r][4]);
    if (ymd) {
      ws[dateCellRef] = {
        t: "n", // numeric serial, not a JS Date
        v: excelSerialFromYMD(ymd.y, ymd.m, ymd.d),
        z: "dd-mmm-yyyy",
      };
    } else {
      ws[dateCellRef] = { t: "s", v: "—" };
    }

    // --- PRICE (column index 5) ---
    const priceCellRef = XLSX.utils.encode_cell({ r: excelRow, c: 5 });
    const priceVal = rows[r][5];
    if (typeof priceVal === "number" && isFinite(priceVal)) {
      ws[priceCellRef] = { t: "n", v: priceVal, z: "#,##0.00" };
    } else {
      ws[priceCellRef] = { t: "s", v: "—" };
    }
  }

  // Create a Notes sheet with disclaimers + branding
  const notesAoa = [
    ["LoRRI — Rate Prediction Tool (RPT)"],
    [""],
    ["Important"],
    [
      "These are indicative rates estimated by Lorri AI using historical freight data, fuel trends, and lane analytics.",
    ],
    [
      "• Assumes current average diesel price; real-time market conditions may vary.",
    ],
    [
      "• Final rates depend on availability, seasonality, and negotiation outcomes.",
    ],
    ["• Use for planning and benchmarking, not as a binding quote."],
    [""],
    ["Contact"],
    ["support@logisticsnow.in | https://logisticsnow.in"],
  ];
  const wsNotes = XLSX.utils.aoa_to_sheet(notesAoa);
  wsNotes["!cols"] = [{ wch: 100 }];

  // Build workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Forecasted Rates");
  XLSX.utils.book_append_sheet(wb, wsNotes, "Notes");

  // File name
  const yyyyMmDd = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `LoRRI_RPT_Forecasts_${yyyyMmDd}.xlsx`);
}

/**
 * Builds and downloads the "Auction History" report as a single-sheet XLSX
 * workbook, replacing the old jsPDF-based PDF download. Generic across the
 * classic and advanced (lane) auction modules — callers assemble the
 * per-type "Auction Details" rows and opt into the advanced-only per-bid
 * Lane/Amount/Quantity breakdown via `includeLaneBreakdown`.
 */
export function exportAuctionHistoryReport({
  filename,
  standardDetailsRows,
  auctionDetailsRows,
  bidList = [],
  formatBidDate,
  includeLaneBreakdown = false,
  sheetName = "Auction History",
}) {
  const aoa = [];
  const numericCells = [];
  const extraMerges = [];

  const pushKeyValueRows = (rows) => {
    rows.forEach(({ label, value, type }) => {
      const row = aoa.length;
      if (type === "currency" || type === "number") {
        const num = typeof value === "number" ? value : Number(value) || 0;
        aoa.push([label, num]);
        numericCells.push({
          row,
          col: 1,
          format: type === "currency" ? "#,##0.00" : "0",
        });
      } else {
        aoa.push([label, value ?? "-"]);
      }
    });
  };

  aoa.push(["Standard Details"]);
  const standardTitleRow = aoa.length - 1;
  pushKeyValueRows(standardDetailsRows);
  aoa.push([]);

  aoa.push(["Auction Details"]);
  const auctionTitleRow = aoa.length - 1;
  pushKeyValueRows(auctionDetailsRows);
  aoa.push([]);

  aoa.push(["Bidding Details"]);
  const biddingTitleRow = aoa.length - 1;

  const bidHeaders = [
    "Sr. No.",
    "LSP Name",
    "LSP Rank",
    "Bid Amount",
    "Bid Status",
    "Bid Date",
    "LSP POC (Number)",
    "IP Address",
  ];
  if (includeLaneBreakdown) bidHeaders.push("Final Offer");
  aoa.push(bidHeaders);
  const bidHeaderRow = aoa.length - 1;

  bidList.forEach((item, index) => {
    const row = aoa.length;
    const dataRow = [
      index + 1,
      item.transporter_name || "-",
      item.rank || "-",
      Number(item.bid_amount) || 0,
      item.is_valid ? "Accepted" : "Rejected",
      formatBidDate(item.timestamp),
      item.contact_number || "-",
      item.ip || "-",
    ];
    if (includeLaneBreakdown) {
      dataRow.push(item.final_rate === true ? "Yes" : "");
    }
    aoa.push(dataRow);
    numericCells.push({ row, col: 3, format: "#,##0.00" });

    if (includeLaneBreakdown && item.option_details?.length > 0) {
      aoa.push(["", "Bid Details"]);
      const subHeaderRow = aoa.length - 1;
      extraMerges.push({
        s: { r: subHeaderRow, c: 1 },
        e: { r: subHeaderRow, c: bidHeaders.length - 1 },
      });
      aoa.push(["", "Lane", "Amount", "Quantity"]);
      item.option_details.forEach((option) => {
        aoa.push([
          "",
          option.option_desc || "-",
          option.option_bid_amount ?? "-",
          option.option_unit_metric ?? "-",
        ]);
      });
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  numericCells.forEach(({ row, col, format }) => {
    const ref = XLSX.utils.encode_cell({ r: row, c: col });
    const v = aoa[row][col];
    ws[ref] = { t: "n", v: typeof v === "number" ? v : 0, z: format };
  });

  ws["!merges"] = [
    { s: { r: standardTitleRow, c: 0 }, e: { r: standardTitleRow, c: 1 } },
    { s: { r: auctionTitleRow, c: 0 }, e: { r: auctionTitleRow, c: 1 } },
    {
      s: { r: biddingTitleRow, c: 0 },
      e: { r: biddingTitleRow, c: bidHeaders.length - 1 },
    },
    ...extraMerges,
  ];

  ws["!cols"] = [
    { wch: 26 },
    { wch: 22 },
    { wch: 10 },
    { wch: 16 },
    { wch: 14 },
    { wch: 22 },
    { wch: 18 },
    { wch: 16 },
  ];
  if (includeLaneBreakdown) ws["!cols"].push({ wch: 12 });

  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: bidHeaderRow, c: 0 },
      e: { r: bidHeaderRow, c: bidHeaders.length - 1 },
    }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}
